'use client'

import { UpdateUserProfileDocument } from '@/gql/generated/graphql'
import { useS3ImageUpload } from '@/hooks/use-s3-image-upload'
import { cn } from '@/lib/utils'
import { Button } from '@components/button'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { LoadingIndicator } from '@components/loading-indicator'
import { usePrivy } from '@privy-io/react-auth'
import { createUserNameSchema } from '@utils/schemas'
import { shallowEqual } from '@xstate/store'
import { useSelector, useStore } from '@xstate/store/react'
import { DetailText } from 'design-configuration/components'
import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useClient } from 'urql'
import { inferFlattenedErrors, z } from 'zod'
import { OnboardingStepFieldLabel } from './onboarding-step-field-label'
import { OnboardingStepHeader } from './onboarding-step-header'
import { FALLBACK_PROFILE_PIC } from '@/app/constants/common'
import { onboardingStore } from './store'
import { SkipForNowButton } from './skip-for-now-button'

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean
}

function Input(props: InputProps) {
  const { error, ...rest } = props

  return (
    <input
      {...rest}
      className={cn(
        'border-outline-variant rounded-xs px-sm py-xs bg-surface border',
        { 'border-error': error },
        rest.className
      )}
    />
  )
}

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean
}

function Textarea(props: TextareaProps) {
  const { error, ...rest } = props

  return (
    <textarea
      {...rest}
      className={cn(
        'border-outline-variant rounded-xs px-sm py-xs bg-surface border',
        { 'border-error': error },
        rest.className
      )}
    />
  )
}

function ProfileImageInput({
  initialImageUrl,
  onChange
}: {
  onChange: (mediaId: number | null) => void
  initialImageUrl?: string
}) {
  const [removed, setRemoved] = useState(false)
  const { handleFileSelect, isUploading, previewUrl, reset } = useS3ImageUpload({
    onUploadComplete: (id) => {
      onChange(id)
      setRemoved(false)
    }
  })
  const inputRef = useRef<HTMLInputElement>(null)

  const getDisplayImage = (removed: boolean, previewUrl: string | null, initialImageUrl?: string) => {
    if (removed) {
      return FALLBACK_PROFILE_PIC
    }
    return previewUrl || initialImageUrl || FALLBACK_PROFILE_PIC
  }

  const hasImage = !removed && !!(previewUrl || initialImageUrl)
  const displayImage = getDisplayImage(removed, previewUrl, initialImageUrl)

  return (
    <div className="gap-sm flex items-center">
      <input
        type="file"
        ref={inputRef}
        className="hidden"
        onChange={(e) => {
          handleFileSelect(e)
          setRemoved(false)
        }}
      />
      <Button
        colorTheme={'outline'}
        onClick={(e) => {
          e.preventDefault()
          if (hasImage) {
            onChange(null)
            reset()
            setRemoved(true)
            if (inputRef.current) {
              inputRef.current.value = ''
            }
          } else {
            inputRef.current?.click()
          }
        }}
        type="button"
      >
        {isUploading ? <LoadingIndicator className="w-sm h-sm" /> : hasImage ? 'Remove' : 'Add'}
      </Button>
      <div className="border-outline-variant relative h-20 w-20 overflow-hidden rounded-full border">
        {typeof displayImage === 'string' ? (
          <ImageWithCustomLoader src={displayImage} alt="Profile image" fill className="object-cover" sizes="16vw" />
        ) : (
          <Image src={FALLBACK_PROFILE_PIC} alt="Profile image" fill className="object-cover" sizes="16vw" />
        )}
      </div>
    </div>
  )
}

export function ProfileSetupStep(): React.ReactNode {
  const urqlClient = useClient()

  const { profile, userDataLoading } = useSelector(
    onboardingStore,
    ({ context }) => ({
      profile: context.profile,
      userDataLoading: context.userDataLoading
    }),
    shallowEqual
  )

  const profileFormSchema = useMemo(() => {
    return z.object({
      profile_picture: z.number().nullable(),
      name: z.string().nullable(),
      username: createUserNameSchema(urqlClient, profile?.id ?? -1),
      bio: z.string().nullable()
    })
  }, [urqlClient, profile?.id])

  // Don't love defining types in the component but since
  // the schema requires the client to be passed in, we need to
  // define the types here.
  type ProfileFormData = z.infer<typeof profileFormSchema>
  type ProfileFieldErrors = inferFlattenedErrors<typeof profileFormSchema>['fieldErrors']

  const initialFormData: ProfileFormData = useMemo(
    () => ({
      profile_picture: profile?.profile_picture_id ?? null,
      name: profile?.name ?? null,
      username: profile?.username ?? '',
      bio: profile?.bio ?? null
    }),
    [profile]
  )

  const store = useStore({
    context: {
      profileId: profile?.id,
      formData: initialFormData,
      errors: {} as ProfileFieldErrors,
      status: 'idle' as 'idle' | 'submitting' | 'success' | 'error'
    },
    on: {
      resetFormData: (ctx, event: { formData: ProfileFormData }) => {
        const { formData } = event

        return {
          ...ctx,
          formData
        }
      },
      updateField: (ctx, event: { name: string; value: string | number | null }) => {
        const { name, value } = event
        const newFormData = { ...ctx.formData, [name]: value }

        const newErrors = { ...ctx.errors }
        delete newErrors[name as keyof ProfileFormData]

        const numErrors = Object.keys(newErrors).length

        return {
          ...ctx,
          formData: newFormData,
          errors: newErrors,
          status: numErrors > 0 ? ('error' as const) : ('idle' as const)
        }
      },
      blurField: (ctx, _event, enqueue) => {
        enqueue.effect(async () => {
          const result = await profileFormSchema.safeParseAsync(ctx.formData)

          if (result.success) {
            return
          }

          store.trigger.setErrors({ errors: result.error.flatten().fieldErrors })
          store.trigger.setStatus({ status: 'error' as const })
        })
        return ctx
      },
      submit: (ctx, _event, enqueue) => {
        enqueue.effect(async () => {
          const result = await profileFormSchema.safeParseAsync(ctx.formData)

          if (!result.success) {
            store.trigger.setStatus({ status: 'error' as const })
            store.trigger.setErrors({ errors: result.error.flatten().fieldErrors })
            return
          }

          const profileId = ctx.profileId
          if (!profileId) {
            return
          }

          const { data, error } = await urqlClient
            .mutation(UpdateUserProfileDocument, {
              pk: profileId,
              updates: {
                profile_picture_id: ctx.formData.profile_picture,
                name: ctx.formData.name,
                username: ctx.formData.username,
                bio: ctx.formData.bio
              }
            })
            .toPromise()

          if (error) {
            console.error(error)
            store.trigger.setStatus({ status: 'error' as const })
            return
          }

          if (data?.update_user_profiles_by_pk) {
            store.trigger.setStatus({ status: 'success' as const })
            onboardingStore.trigger.profileSetupStepCompleted({ profile: data.update_user_profiles_by_pk })
          }
        })

        return {
          ...ctx,
          status: 'submitting' as const
        }
      },
      setStatus: (ctx, event: { status: 'idle' | 'submitting' | 'success' | 'error' }) => {
        const { status } = event

        return {
          ...ctx,
          status
        }
      },
      setErrors: (ctx, event: { errors: ProfileFieldErrors }) => {
        const { errors } = event

        return {
          ...ctx,
          errors
        }
      }
    }
  })

  // Use selectors to get the pieces of state we need for rendering.
  // The component will only re-render if these specific values change.
  const formData = useSelector(store, (state) => state.context.formData, shallowEqual)

  const errors = useSelector(store, (state) => state.context.errors)
  const status = useSelector(store, (state) => state.context.status)

  useEffect(() => {
    store.trigger.resetFormData({ formData: initialFormData })
  }, [initialFormData, store.trigger])

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    store.trigger.updateField({ name: e.target.name, value: e.target.value })
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (status === 'error') {
      return
    }

    store.trigger.submit()
  }

  const { linkEmail, user } = usePrivy()
  const email = user?.email

  return (
    <div className="gap-md flex min-h-full flex-col">
      <OnboardingStepHeader
        title="Make your profile yours"
        subtitle="Tell your story as a collector. You can always update this later."
      />
      <AnimatePresence mode="wait">
        {userDataLoading ? (
          <motion.div
            className="flex h-full flex-1 flex-col items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LoadingIndicator className="fill-outline-variant h-10 w-10" />
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ visualDuration: 1 }}
            className="flex flex-1 flex-col"
          >
            <form className="gap-md flex flex-1 flex-col" onSubmit={handleSubmit}>
              <div className="px-md flex-1">
                <div className="py-sm gap-sm flex flex-col md:flex-row md:items-center md:justify-between">
                  <OnboardingStepFieldLabel
                    label="Connect your email"
                    description="Connect your email for account access and updates. Your email stays private."
                  />
                  <Button
                    colorTheme={'outline'}
                    onClick={(e) => {
                      e.preventDefault()
                      if (email) {
                        return
                      }

                      linkEmail()
                    }}
                    type="button"
                    className="self-start md:self-auto"
                    disabled={Boolean(email)}
                  >
                    {email ? email.address : 'Connect email'}
                  </Button>
                </div>
                <div className="py-sm gap-sm flex flex-col md:flex-row md:items-center">
                  <OnboardingStepFieldLabel
                    label="Profile image"
                    description="Choose how you want to appear to the community."
                    htmlFor="profile-image"
                    className="flex-1"
                  />
                  <ProfileImageInput
                    initialImageUrl={profile?.profile_picture?.url ?? undefined}
                    onChange={(mediaId) => {
                      store.trigger.updateField({ name: 'profile_picture', value: mediaId })
                    }}
                  />
                </div>
                <div className="py-sm">
                  <OnboardingStepFieldLabel
                    label="Username"
                    description="Your unique handle for your profile URL and display."
                    htmlFor="username"
                    className="mb-xs"
                  />
                  <Input
                    name="username"
                    className="w-full"
                    onChange={handleFieldChange}
                    value={formData.username ?? undefined}
                    error={Boolean(errors.username)}
                    onBlur={() => store.trigger.blurField()}
                  />
                  {Boolean(errors.username) && <DetailText className="text-error mt-xs">{errors.username}</DetailText>}
                </div>
                <div className="py-sm">
                  <OnboardingStepFieldLabel
                    label="Display name"
                    description="How you'll be known as a collector."
                    htmlFor="name"
                    className="mb-xs"
                  />
                  <Input
                    name="name"
                    className="w-full"
                    onChange={handleFieldChange}
                    value={formData.name ?? undefined}
                    error={Boolean(errors.name)}
                    onBlur={() => store.trigger.blurField()}
                  />
                  {Boolean(errors.name) && <DetailText className="text-error mt-xs">{errors.name}</DetailText>}
                </div>
                <div className="py-sm">
                  <OnboardingStepFieldLabel
                    label="Bio"
                    description="Share your story with the community."
                    htmlFor="bio"
                    className="mb-xs"
                  />
                  <Textarea
                    name="bio"
                    rows={4}
                    className="w-full"
                    onChange={handleFieldChange}
                    value={formData.bio ?? undefined}
                    error={Boolean(errors.bio)}
                    onBlur={() => store.trigger.blurField()}
                  />
                  {Boolean(errors.bio) && <DetailText className="text-error mt-xs">{errors.bio}</DetailText>}
                </div>
              </div>
              <div className="p-md flex items-center justify-between">
                <Button
                  colorTheme={'secondary'}
                  branded
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    onboardingStore.trigger.back()
                  }}
                >
                  Back
                </Button>
                <div className="gap-md flex items-center">
                  <SkipForNowButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      onboardingStore.trigger.skipStep()
                    }}
                  />
                  <Button
                    colorTheme={'primary'}
                    branded
                    type="submit"
                    disabled={['submitting', 'error'].includes(status)}
                  >
                    {status === 'submitting' ? <LoadingIndicator className="w-sm h-sm fill-outline-variant" /> : 'Next'}
                  </Button>
                </div>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
